import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DatePicker, Flex, Input, TimePicker, Typography, Image, Select } from 'antd';
import { useMachineDetailsRobotSchedule } from '../../../../context/MachineDetailsRobotScheduleContext';
import { useScheduleUpsertModalCV50 } from '../../../../context/ScheduleCreateModalContext';
import { getSelectedMapFromSelectOption, MAP_GROUP_NAME_OTHERS } from '../../../../utils/map';
import { CleaningScheduleCard } from '../CleaningScheduleCard/CleaningScheduleCard';
import { IconContainer, RobotNameText, RobotTypeText, ScheduleFormInfoFormItem } from './ScheduleFormInfo.styles';
import { kccThemeConstants } from 'config/theme/kccTheme/kccTheme';
import { SvgIcon } from 'lib/components/SvgIcon/SvgIcon';
import { DateTime } from 'lib/utils/date-handling/DateTime';
import { DATE_FORMAT_PATTERN } from 'lib/utils/date-handling/DateTime.types';

export const ScheduleFormInfoCV50 = (): JSX.Element => {
  const { t, i18n } = useTranslation();

  const { isScheduleCreateFullSizeOpen, isReadOnly } = useMachineDetailsRobotSchedule();

  const {
    robot,
    scheduleMap,
    scheduleMapGroups,
    isScheduleMapsLoading,
    isDropdownVisible,
    handleScheduleMapChange,
    hasErrorScheduleName,
    hasErrorScheduleMap,
    handleValidateFields,
    handleDropdownVisibleChange,
    handleEditScheduleMap,
  } = useScheduleUpsertModalCV50();

  const mapSelectOptions = useMemo(
    () =>
      scheduleMapGroups.map(group => {
        const groupLabel = group.groupName === MAP_GROUP_NAME_OTHERS ? t('common.others') : group.groupName;

        return {
          key: group.groupId,
          label: groupLabel,
          title: groupLabel,
          options: group.maps.map(map => ({
            label: (
              <Flex align="center" gap={kccThemeConstants.SPACING.XS}>
                <SvgIcon name="map" />
                <Typography.Text style={{ lineHeight: '20px' }}>{map.name}</Typography.Text>
              </Flex>
            ),
            searchLabel: map.name,
            value: map.id,
            map,
          })),
        };
      }),
    [scheduleMapGroups, t]
  );

  const mapSelectSuffixIcon = useMemo(() => {
    if (isScheduleMapsLoading) {
      return undefined;
    }

    if (isDropdownVisible) {
      return <SvgIcon name="search" />;
    }

    return <SvgIcon name="chevronDown" />;
  }, [isScheduleMapsLoading, isDropdownVisible]);

  return (
    <Flex vertical gap={kccThemeConstants.SPACING.BASE}>
      {isScheduleCreateFullSizeOpen && (
        <>
          <Flex
            align="center"
            gap={kccThemeConstants.SPACING.SM}
            style={{ marginBottom: hasErrorScheduleName ? kccThemeConstants.SPACING.BASE : 0 }}
          >
            <IconContainer>
              <SvgIcon name="calendar" />
            </IconContainer>

            <ScheduleFormInfoFormItem
              name="scheduleName"
              rules={[{ required: true, message: t('createSchedule.scheduleNameRequired') }]}
            >
              <Input
                placeholder={t('createSchedule.scheduleNamePlaceholder')}
                onChange={(): void => handleValidateFields(['scheduleName'])}
              />
            </ScheduleFormInfoFormItem>
          </Flex>

          <Flex align="center" gap={kccThemeConstants.SPACING.SM}>
            <IconContainer>
              <SvgIcon name="robotNew" />
            </IconContainer>

            <ScheduleFormInfoFormItem>
              <Flex gap={kccThemeConstants.SPACING.SM} align="center" style={{ width: '100%' }}>
                <Image src={robot.variant?.picture.product} alt={robot.name} width={48} height={48} preview={false} />

                <Flex vertical gap={2}>
                  <RobotNameText>{robot.name}</RobotNameText>
                  <RobotTypeText>{robot.type?.name}</RobotTypeText>
                </Flex>
              </Flex>
            </ScheduleFormInfoFormItem>
          </Flex>
        </>
      )}

      <Flex
        align={scheduleMap ? 'flex-start' : 'center'}
        gap={kccThemeConstants.SPACING.SM}
        style={{ marginBottom: hasErrorScheduleMap ? kccThemeConstants.SPACING.BASE : 0 }}
      >
        <IconContainer>
          <SvgIcon name="map" />
        </IconContainer>

        <ScheduleFormInfoFormItem
          name="scheduleMap"
          rules={[{ required: true, message: t('createSchedule.mapRequired') }]}
        >
          {!scheduleMap ? (
            <Select
              placeholder={t('createSchedule.mapPlaceholder')}
              allowClear={false}
              options={mapSelectOptions}
              optionFilterProp="searchLabel"
              showSearch
              loading={isScheduleMapsLoading}
              disabled={isScheduleMapsLoading || isReadOnly}
              defaultOpen={isDropdownVisible && !isReadOnly}
              autoFocus={isDropdownVisible && !isReadOnly}
              open={isReadOnly ? false : isDropdownVisible}
              onDropdownVisibleChange={(visible): void => {
                if (isReadOnly) {
                  return;
                }

                handleDropdownVisibleChange(visible);
              }}
              suffixIcon={mapSelectSuffixIcon}
              onSelect={(_value, option): void => {
                if (isReadOnly) {
                  return;
                }

                const selected = getSelectedMapFromSelectOption(option);

                if (selected) {
                  handleValidateFields(['scheduleMap']);
                  handleScheduleMapChange(selected);
                }
              }}
            />
          ) : (
            <CleaningScheduleCard isB50={false} selectedSchedule={scheduleMap} onEdit={handleEditScheduleMap} />
          )}
        </ScheduleFormInfoFormItem>
      </Flex>

      <Flex align="center" gap={kccThemeConstants.SPACING.SM}>
        <IconContainer>
          <SvgIcon name="clock" />
        </IconContainer>

        <Flex gap={kccThemeConstants.SPACING.SM} style={{ width: '100%' }}>
          <ScheduleFormInfoFormItem
            name="scheduleDate"
            rules={[{ required: true, message: t('createSchedule.scheduleDateRequired') }]}
            style={{ flex: 1, margin: 0 }}
          >
            <DatePicker
              popupClassName="kcc-theme-date-picker-popup"
              allowClear={false}
              style={{ width: '100%', height: '40px' }}
              suffixIcon={<SvgIcon name="calendar" />}
              format={(date): string =>
                DateTime.formatDateByLocale(i18n.language, date.toISOString(), DATE_FORMAT_PATTERN.DATE)
              }
              disabled
            />
          </ScheduleFormInfoFormItem>

          <ScheduleFormInfoFormItem
            name="scheduleTime"
            rules={[{ required: true, message: t('createSchedule.scheduleTimeRequired') }]}
            style={{ flex: 1, margin: 0 }}
          >
            <TimePicker
              popupClassName="kcc-theme-time-picker-popup"
              format="HH:mm"
              allowClear={false}
              style={{ width: '100%', height: '40px' }}
              className="schedule-picker-active-border"
              suffixIcon={<SvgIcon name="clock" />}
              minuteStep={15}
              disabled={isReadOnly}
            />
          </ScheduleFormInfoFormItem>
        </Flex>
      </Flex>
    </Flex>
  );
};
